import { useEffect, useState } from "react";
import { UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createVisitor, MOTIFS } from "@/lib/api";
const EMPTY = {
  nom: "",
  societe: "",
  telephone: "",
  personne_visitee: "",
  service: "",
  motif: "Rendez-vous",
  motif_autre: "",
  piece_identite: "",
  badge: "",
  plaque: "",
  observations: "",
};

const Field = ({ label, required, children, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
  </div>
);

export const ArrivalForm = ({ agent, motifs, services, siteId, siteName }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const motifOptions = motifs && motifs.length ? motifs : MOTIFS;
  const serviceOptions = services || [];

  useEffect(() => {
    if (motifOptions.length && !motifOptions.includes(form.motif)) {
      setForm((f) => ({ ...f, motif: motifOptions[0] }));
    }
  }, [motifOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.personne_visitee.trim()) {
      toast.error("Le nom du visiteur et la personne visitée sont obligatoires.");
      return;
    }
    if (form.motif === "Autre" && !form.motif_autre.trim()) {
      toast.error("Veuillez préciser le motif de la visite.");
      return;
    }
    setSubmitting(true);
    try {
      await createVisitor({
        ...form,
        enregistre_par: agent || "Poste d'accueil",
        site_id: siteId || "",
        site_name: siteName || "",
      });
      toast.success(`Arrivée enregistrée : ${form.nom}`);
      setForm((f) => ({ ...EMPTY, motif: motifOptions[0] || "Rendez-vous" }));
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "min-h-[46px] text-base bg-slate-50 border-slate-300 focus-visible:ring-[#D4AF37]";

  return (
    <form
      onSubmit={submit}
      className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden"
      data-testid="arrival-form"
    >
      <div className="bg-[#0A192F] px-5 py-4 flex items-center gap-3 border-b-2 border-[#D4AF37]">
        <UserPlus className="h-5 w-5 text-[#D4AF37]" />
        <h2 className="font-serif-title text-lg font-bold text-white">
          Enregistrer une arrivée
        </h2>
      </div>

      <div className="p-5 space-y-4">
        <Field label="Nom complet du visiteur" required testid="field-nom">
          <Input
            className={inputCls}
            placeholder="Ex : Amadou Diallo"
            value={form.nom}
            onChange={set("nom")}
            data-testid="input-nom"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Société / organisme" testid="field-societe">            <Input
              className={inputCls}
              placeholder="Optionnel"
              value={form.societe}
              onChange={set("societe")}
              data-testid="input-societe"
            />
          </Field>
          <Field label="Téléphone du visiteur" testid="field-telephone">
            <Input
              type="tel"
              className={`${inputCls} font-mono-data`}
              placeholder="Ex : +224 6XX XX XX XX"
              value={form.telephone}
              onChange={set("telephone")}
              data-testid="input-telephone"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Personne à visiter" required testid="field-personne">
            <Input
              className={inputCls}
              placeholder="Ex : M. Camara"
              value={form.personne_visitee}
              onChange={set("personne_visitee")}
              data-testid="input-personne"
            />
          </Field>
          <Field label="Service / département" testid="field-service">
            <Select value={form.service || "__none__"} onValueChange={(v) => set("service")(v === "__none__" ? "" : v)}>
              <SelectTrigger className={inputCls} data-testid="select-service">
                <SelectValue placeholder="Choisir un service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Aucun —</SelectItem>
                {serviceOptions.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Motif de la visite" required testid="field-motif">
            <Select value={form.motif} onValueChange={set("motif")}>
              <SelectTrigger className={inputCls} data-testid="select-motif">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {motifOptions.map((m) => (
                  <SelectItem key={m} value={m} data-testid={`motif-option-${m}`}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {form.motif === "Autre" ? (
            <Field label="Préciser le motif" required testid="field-motif-autre">
              <Input
                className={inputCls}
                placeholder="Motif libre"
                value={form.motif_autre}
                onChange={set("motif_autre")}
                data-testid="input-motif-autre"
              />
            </Field>
          ) : (
            <Field label="N° de badge remis" testid="field-badge">
              <Input
                className={`${inputCls} font-mono-data`}
                placeholder="Ex : B-104"
                value={form.badge}
                onChange={set("badge")}
                data-testid="input-badge"
              />
            </Field>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {form.motif === "Autre" && (
            <Field label="N° de badge remis" testid="field-badge">
              <Input
                className={`${inputCls} font-mono-data`}
                placeholder="Ex : B-104"
                value={form.badge}
                onChange={set("badge")}
                data-testid="input-badge"
              />
            </Field>
          )}
          <Field label="N° pièce d'identité" testid="field-piece">
            <Input
              className={`${inputCls} font-mono-data`}
              placeholder="CNI / Passeport"
              value={form.piece_identite}
              onChange={set("piece_identite")}
              data-testid="input-piece"
            />
          </Field>
          <Field label="Plaque d'immatriculation" testid="field-plaque">
            <Input
              className={`${inputCls} font-mono-data uppercase`}
              placeholder="Si véhicule"
              value={form.plaque}
              onChange={set("plaque")}
              data-testid="input-plaque"
            />
          </Field>
        </div>

        <Field label="Commentaire / observations" testid="field-observations">
          <Textarea
            className="bg-slate-50 border-slate-300 focus-visible:ring-[#D4AF37] min-h-[70px] text-base"
            placeholder="Commentaire ou remarque particulière (optionnel)"
            value={form.observations}
            onChange={set("observations")}
            data-testid="input-observations"
          />
        </Field>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full min-h-[52px] text-base bg-[#0A192F] hover:bg-[#1E293B] text-white font-bold rounded-lg border-b-2 border-[#D4AF37] shadow hover:shadow-lg transition-all"
          data-testid="submit-arrival-btn"
        >
          {submitting ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <UserPlus className="h-5 w-5 mr-2" /> Enregistrer l'arrivée
            </>
          )}
        </Button>
        <p className="text-xs text-center text-slate-400">
          L'heure d'arrivée est horodatée automatiquement à la soumission.
        </p>
      </div>
    </form>
  );
};
